import moment from 'moment';
import { REPORT_COLORS } from '@/components/charts/ReportCharts';
import { storedCallStatus } from '@/utilities/callStatus';
import { isPseudoCdrRow } from '@/utilities/pseudoCdr';
import { isMissedIvrRow } from '@/utilities/missedIvr';
import type { CallRow } from './useCallReportLoader';

export type CallStatusKey = 'connected' | 'failed' | 'abandoned';

export const CALL_STATUS_TEXT: Record<CallStatusKey, string> = {
  connected: 'Connected',
  failed: 'Failed',
  abandoned: 'Abandoned',
};

const STATUS_COLOR: Record<CallStatusKey, string> = {
  connected: REPORT_COLORS.good,
  failed: REPORT_COLORS.crit,
  abandoned: REPORT_COLORS.warn,
};

export const CALL_STATUS_SERIES = (Object.keys(CALL_STATUS_TEXT) as CallStatusKey[]).map((key) => ({
  key,
  label: CALL_STATUS_TEXT[key],
  color: STATUS_COLOR[key],
}));

export const talkSeconds = (row: CallRow): number => Number(row.patch_duration) || 0;

export const callStatusKey = (row: CallRow): CallStatusKey =>
  (storedCallStatus(row) as CallStatusKey | null) ?? (talkSeconds(row) > 0 ? 'connected' : 'failed');

export const CALL_DURATION_BUCKETS = [
  { label: '0 sec', match: (seconds: number) => seconds === 0 },
  { label: '1-30 sec', match: (seconds: number) => seconds >= 1 && seconds <= 30 },
  { label: '31-120 sec', match: (seconds: number) => seconds >= 31 && seconds <= 120 },
  { label: '120+ sec', match: (seconds: number) => seconds > 120 },
];

export const talkBucket = (row: CallRow): string =>
  CALL_DURATION_BUCKETS.find((b) => b.match(talkSeconds(row)))?.label ?? '';

export function buildCallStats(rows: CallRow[]) {
  const stats = { total: rows.length, connected: 0, failed: 0, abandoned: 0, pseudo: 0, missedIvr: 0 };
  rows.forEach((r) => {
    stats[callStatusKey(r)] += 1;
    if (isPseudoCdrRow(r)) stats.pseudo += 1;
    if (isMissedIvrRow(r)) stats.missedIvr += 1;
  });
  return stats;
}

type StatusCounts = Record<CallStatusKey, number>;
const zero = (): StatusCounts => ({ connected: 0, failed: 0, abandoned: 0 });

export function buildCallCharts(rows: CallRow[], topAgents: number, endReason: (row: CallRow) => string) {
  const byStatus = zero();
  const byBucket = new Map<string, number>(CALL_DURATION_BUCKETS.map((b) => [b.label, 0]));
  const perHour = Array.from({ length: 24 }, (_, h) => ({ hour: `${String(h).padStart(2, '0')}:00`, ...zero() }));
  const byDay = new Map<string, { day: string } & StatusCounts>();
  const byAgent = new Map<string, StatusCounts>();
  const byReason = new Map<string, number>();
  let incoming = 0;
  let outgoing = 0;

  rows.forEach((r) => {
    const key = callStatusKey(r);
    byStatus[key] += 1;
    if (r.call_type === 'incoming') incoming += 1;
    else if (r.call_type === 'outgoing') outgoing += 1;
    const bucket = talkBucket(r);
    if (bucket) byBucket.set(bucket, (byBucket.get(bucket) || 0) + 1);

    const m = moment(String(r.start_time || ''), ['YYYY-MM-DD HH:mm:ss', moment.ISO_8601]);
    if (m.isValid()) {
      perHour[m.hour()]![key] += 1;
      const dayKey = m.format('YYYY-MM-DD');
      const day = byDay.get(dayKey) || { day: m.format('DD MMM'), ...zero() };
      day[key] += 1;
      byDay.set(dayKey, day);
    }

    const agent = String(r.agent_name || '').trim() || 'No agent';
    const a = byAgent.get(agent) || zero();
    a[key] += 1;
    byAgent.set(agent, a);

    const reason = endReason(r) || 'Unknown';
    byReason.set(reason, (byReason.get(reason) || 0) + 1);
  });

  const sum = (c: StatusCounts) => c.connected + c.failed + c.abandoned;
  return {
    status: CALL_STATUS_SERIES.map((s) => ({ name: s.label, value: byStatus[s.key], color: s.color })),
    direction: [
      { name: 'Incoming', value: incoming, color: REPORT_COLORS.call },
      { name: 'Outgoing', value: outgoing, color: REPORT_COLORS.good },
      { name: 'Other', value: rows.length - incoming - outgoing, color: REPORT_COLORS.off },
    ],
    talkBuckets: [...byBucket.entries()].map(([name, value], i) => ({
      name,
      value,
      color: [REPORT_COLORS.off, REPORT_COLORS.warn, REPORT_COLORS.call, REPORT_COLORS.good][i] || REPORT_COLORS.info,
    })),
    perHour,
    perDay: [...byDay.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([, v]) => v),
    byAgent: [...byAgent.entries()]
      .sort((a, b) => sum(b[1]) - sum(a[1]))
      .slice(0, topAgents || Infinity)
      .map(([agent, v]) => ({ agent, ...v })),
    agentCount: byAgent.size,
    endReasons: [...byReason.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([reason, calls]) => ({ reason, calls })),
    reasonCount: byReason.size,
  };
}
